import logError from 'src/data/services/logError';
import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Platform, RefreshControl } from 'react-native';
import _ from 'lodash';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import checkIsEven from 'src/core/utils/checkIsEven';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Card from 'src/presentation/components/atoms/Card';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Loader from 'src/presentation/components/atoms/Loader';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import EventDetailLink from 'src/presentation/components/molecules/EventDetailLink';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import type Constants from 'src/core/utils/Constants';
import type { ApiPageTimetableHolder } from 'src/core/models/api/ApiPage';

function TimetableHolderScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenTimetableHolder>>();
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();
	const [isEventLoading, setIsEventLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const footerHeight = useFooterHeight();

	// The timetables themselves come from the event in the store; the page only carries the title.
	const { page, isLoading: isPageLoading } = usePageFromRoute(route?.params, { type: 'TimetableHolder' });
	const title = page?.name ?? '';

	// '' is the "no active event" sentinel, so truthiness is intended here.
	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	const activeEventUuid = navigationStore.activeEventUuid || eventStore.activeEventUuid || '';

	// Keyed on the uuid so a cold URL still loads: the event is activated from the path a tick
	// after this screen mounts. `finally` rather than a then, because a rejected fetch used to
	// leave the screen on its loader with nothing to clear it.
	useEffect(() => {
		if (!activeEventUuid) {
			return;
		}

		eventStore
			.fetchEvent(activeEventUuid)
			.catch(logError)
			.finally(() => {
				setIsEventLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeEventUuid]);

	const handleRefresh = async () => {
		setRefreshing(true);
		await eventStore.fetchEvent(navigationStore.activeEventUuid ?? '', true);
		setRefreshing(false);
	};

	const renderTimetables = () => {
		const timeTables = eventStore.activeEventView.find((p): p is ApiPageTimetableHolder => p.type === 'TimetableHolder');
		if (!timeTables || timeTables.children.length === 0) {
			return (
				<EmptyState
					icon="time-outline"
					message={Translations.textNoTimetableMessage}
					title={Translations.textNoTimetableTitle}
				/>
			);
		}

		return timeTables.children.map((timeTable, index: number) => (
			<EventDetailLink
				data={timeTable}
				key={index}
				spaceAround={{
					marginRight: checkIsEven(index) ? (Platform.OS === 'ios' ? 30 : 16) : 0,
				}}
				theme={eventStore.activeTheme}
			/>
		));
	};

	return (
		<View renderToHardwareTextureAndroid={true} style={styles.layoutContainer}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>

			{isEventLoading || isPageLoading ? (
				<Loader />
			) : (
				<ScrollView
					contentContainerStyle={{ flexGrow: 1, paddingBottom: footerHeight }}
					refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
					style={{ flex: 1 }}
				>
					<Card backgroundColor={eventStore.activeTheme.app_bg_color} backgroundImage={eventStore.activeTheme.app_bg_image}>
						<View style={styles.wrapContainer}>
							<View style={styles.linkContainer}>{renderTimetables()}</View>
						</View>
					</Card>
				</ScrollView>
			)}

			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default TimetableHolderScreen;

const styles = StyleSheet.create({
	wrapContainer: {
		// Stretch (not center) so the link grid fills the width and a single tile sits top-left at position 1
		// instead of being centered.
		alignItems: 'stretch',
	},
	linkContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		paddingTop: 28,
		paddingLeft: Dimens.padding40,
		paddingRight: Dimens.padding40,
	},
	layoutContainer: {
		flex: 1,
		backgroundColor: Platform.OS === 'android' ? Colors.white : undefined,
	},
});
